import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { useCloseWithEscKey, useOnClickOutside } from '../helpers'
import BasicButton from './BasicButton'
import CloseIcon from './CloseIcon'
import Image from './Image'

type DialogProps = {
  imageAlt?: string
  imageFallbackUrls?: string[]
  imageHeight?: number | null
  imageSizes?: string
  imageSrcSet?: string
  imageUrl?: string | null
  imageWidth?: number | null
  onClose: () => void
  open?: boolean
  title: React.ReactNode
}

const Dialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  children,
  imageAlt = '',
  imageFallbackUrls = [],
  imageHeight = null,
  imageSizes = undefined,
  imageSrcSet = '',
  imageUrl = null,
  imageWidth = null,
  onClose,
  open = false,
  title,
}) => {
  const wrapperRef = React.useRef<HTMLInputElement>(null)
  const [currentImageUrl, setCurrentImageUrl] = React.useState<string | null>(imageUrl)
  const [useResponsiveSources, setUseResponsiveSources] = React.useState(true)

  useOnClickOutside(wrapperRef, onClose)
  useCloseWithEscKey(onClose)

  React.useEffect(() => {
    setCurrentImageUrl(imageUrl)
    setUseResponsiveSources(true)
  }, [imageSrcSet, imageUrl])

  const onImageError = () => {
    const currentFallbackIndex = imageFallbackUrls.indexOf(currentImageUrl as string)
    const fallbackUrl =
      currentFallbackIndex === -1
        ? imageFallbackUrls[0]
        : imageFallbackUrls.slice(currentFallbackIndex + 1)[0]

    if (fallbackUrl) {
      setCurrentImageUrl(fallbackUrl)
      setUseResponsiveSources(false)
    }
  }

  React.useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      if (open) {
        body.classList.add('overflow-hidden')
      } else {
        body.classList.remove('overflow-hidden')
      }
    }
    return () => {
      const body = document.querySelector('body')
      if (body) {
        body.classList.remove('overflow-hidden')
      }
    }
  }, [open])

  if (!open) {
    return null
  }

  return ReactDOM.createPortal(
    <div
      aria-labelledby="modal-title"
      aria-modal="true"
      className="relative z-10 inset-0"
      role="dialog"
    >
      <div className="fixed inset-0 bg-muted bg-opacity-50 transition-opacity"></div>
      <div className="fixed z-10 inset-0 overflow-y-auto overscroll-auto">
        <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
          <div
            className={`relative bg-light rounded-3xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 ${
              currentImageUrl ? 'w-full max-w-3xl' : ''
            }`}
          >
            <div className="bg-light p-8" ref={wrapperRef}>
              <div className="flex justify-between mb-6 text-dark">
                <h3 className="text-subcategory select-none">{title}</h3>
                <BasicButton className="hover:text-theme-main" clearBasicStyle onClick={onClose}>
                  <CloseIcon />
                </BasicButton>
              </div>
              {currentImageUrl && (
                <div className="mb-6 w-full overflow-hidden rounded-2xl bg-muted">
                  <Image
                    alt={imageAlt}
                    className="max-h-[60vh] w-full object-contain"
                    height={imageHeight ?? undefined}
                    key={`${currentImageUrl}|${useResponsiveSources ? imageSrcSet : ''}`}
                    onError={onImageError}
                    sizes={useResponsiveSources && imageSrcSet ? imageSizes : undefined}
                    src={currentImageUrl}
                    srcSet={useResponsiveSources && imageSrcSet ? imageSrcSet : undefined}
                    width={imageWidth ?? undefined}
                  />
                </div>
              )}
              <div>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Dialog
